import {
  Button,
  ButtonGroup,
  Input,
  FormGroup,
  Label,
  Select,
} from '@/components'
import { useAxios } from '@/context/axiosContext'
import { useEffect, useState, useCallback } from 'react'
import DataTable, { TableColumn } from 'react-data-table-component'
import { useNavigate } from 'react-router-dom'
import { formatDate } from '@/utils/date'
import commonUtils from '@/utils/commonUtils'
import { tableStyles } from '@/styles/dataTable/style'
import { FlexWrapper } from '@/components/layout/flex/flexWrapper'
import { useRedis } from '@/hooks/useRedis'

type TInput = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

interface DataRow {
  rowNum: string
  msgCd: string
  msgExp: string
  useYn: string
  typeDivCd: string
  regId: string
  regDate: string
  updId: string
  updDate: string
}

const MessageMgmt: React.FC = () => {
  const axios = useAxios()
  const navigate = useNavigate()
  const { getComCodeValue } = useRedis()
  const [data, setData] = useState<DataRow[]>([])

  const [srchData, setSrchData] = useState({
    srchOpt: '',
    srchStr: '',
    useYn: '',
    typeDivCd: '',
  })

  const titleRefactoring = (title: string) => {
    if (title.length > 40) return title.substring(0, 35) + '...'
    else return title
  }
  const columns: TableColumn<DataRow>[] = [
    {
      name: 'No.',
      selector: (row) => row.rowNum,
      sortable: true,
      center: true,
      wrap: true,
      width: '5%',
      cell: (row) => (
        <div style={{ width: '100%', textAlign: 'right' }}>{row.rowNum}</div>
      ),
    },
    {
      name: '메시지코드',
      selector: (row) => row.msgCd,
      sortable: true,
      wrap: true,
      width: '10%',
    },
    {
      name: '메시지내용',
      selector: (row) => titleRefactoring(row.msgExp),
      sortable: true,
      center: false,
      width: '30%',
    },
    {
      name: '사용여부',
      selector: (row) => getComCodeValue('USE_YN', row.useYn)?.codeNm || '',
      sortable: true,
      center: true,
      wrap: true,
      width: '10%',
    },
    {
      name: '메시지구분코드',
      selector: (row) =>
        getComCodeValue('TYPE_DIV_CD', row.typeDivCd)?.codeNm || '',
      sortable: true,
      center: true,
      wrap: true,
      width: '10%',
    },
    {
      name: '수정자',
      selector: (row) => row.updId,
      sortable: true,
      center: true,
      wrap: true,
      width: '10%',
    },
    {
      name: '수정일시',
      format: (row) => commonUtils.formatDate(row.updDate, false),
      selector: (row) => row.updDate,
      sortable: true,
      wrap: true,
      width: '15%',
    },
    {
      name: '',
      cell: (row) => (
        <>
          <Button
            variant="outlined"
            color="neverbe"
            size="xs"
            onClick={() => handleButtonUpdateClick(row.msgCd)}
          >
            수정
          </Button>
        </>
      ),
      sortable: false,
      width: '10%',
    },
  ]

  const handleSearch = (e: React.ChangeEvent<TInput>) => {
    const { name, value } = e.target as HTMLInputElement | HTMLTextAreaElement // 타입 캐스팅
    setSrchData((prev) => ({ ...prev, [name]: value }))
  }

  const handleButtonUpdateClick = async (msgCd: string) => {
    navigate(`/messageMgmt/detail`, {
      state: {
        msgCd: msgCd,
      },
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      getMessageList()
    }
  }

  const getMessageList = useCallback(async () => {
    try {
      const params: any = {}
      if (srchData.srchOpt.trim()) {
        params.srchOpt = srchData.srchOpt
      }
      if (srchData.srchStr.trim()) {
        params.srchStr = srchData.srchStr
      }
      if (srchData.useYn) {
        params.useYn = srchData.useYn
      }
      if (srchData.typeDivCd) {
        params.typeDivCd = srchData.typeDivCd
      }

      const response = await axios.get('/messageMgmt/messageList', { params })
      const common = response.data.common
      const data = response.data.data
      let listData = undefined

      if (common.resultCode == 'NOTICES0200') {
        listData = data.messageList
      }

      if (Array.isArray(listData)) {
        setData(listData)
      }
    } catch (error) {
      console.error('Error fetching message list:', error)
    }
  }, [axios, srchData]) // 의존성 배열에 srchData 추가

  const handleSearchClick = () => {
    getMessageList()
  }

  useEffect(() => {
    getMessageList()
  }, []) // srchData가 변경될 때마다 실행

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          marginBottom: '10px',
          alignItems: 'center',
          marginTop: '50px',
        }}
      >
        <p style={{ marginLeft: '20px', fontSize: '20px' }}>메시지 관리</p>

        <div style={{ marginLeft: 'auto' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <FormGroup>
              <Label htmlFor={'useYn'}>사용여부</Label>
              <Select
                id="useYn"
                name="useYn"
                width="100px"
                onChange={handleSearch}
              >
                <option value="">전체</option>
                <option value="Y">사용</option>
                <option value="N">미사용</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor={'typeDivCd'}>메시지구분</Label>
              <Select
                id="typeDivCd"
                name="typeDivCd"
                width="100px"
                onChange={handleSearch}
              >
                <option value="">전체</option>
                <option value="0">에러</option>
                <option value="1">기본</option>
                <option value="2">알림</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor={'srchOpt'}>검색종류</Label>
              <FlexWrapper gap="10px">
                <Select
                  id="srchOpt"
                  name="srchOpt"
                  width="100px"
                  onChange={handleSearch}
                >
                  <option value="00">전체</option>
                  <option value="01">코드</option>
                  <option value="02">내용</option>
                </Select>
                <Input
                  placeholder="제목 및 내용 검색 가능"
                  width="200px"
                  id="srchStr"
                  name="srchStr"
                  value={srchData.srchStr}
                  onChange={handleSearch}
                  onKeyDown={handleKeyDown}
                />
              </FlexWrapper>
            </FormGroup>

            <FormGroup>
              <Label htmlFor={''}>
                <></>
              </Label>
              <FlexWrapper gap="10px">
                <Button
                  onClick={handleSearchClick}
                  color="neverbe"
                  variant="outlined"
                >
                  검색
                </Button>
                <Button
                  onClick={() => {
                    navigate('/messageMgmt/detail')
                  }}
                  color="neverbe"
                >
                  등록
                </Button>
              </FlexWrapper>
            </FormGroup>
          </div>
          <div style={{ color: 'red', marginTop: '5px', textAlign: 'right' }}>
            * 옵션 선택 후 검색어 없이 조회 가능
          </div>
        </div>
      </div>
      <DataTable
        // TODO 페이징처리는 서버 vs 클라이언트 (현행 클라이언트)
        columns={columns}
        data={data}
        customStyles={tableStyles}
        pagination={true}
        paginationPerPage={10}
        paginationRowsPerPageOptions={[10, 20, 30, 50, 100, 500]}
        noDataComponent={<>조회결과가 없습니다.</>}
      />
    </>
  )
}
export default MessageMgmt
